import React, { FC, useState, useRef, useEffect } from "react";
import { PinterestButton, FacebookButton } from "react-social";
import ClipboardJS from "clipboard";

import AppLink from "../AppLink";

import { isIOSDevice } from "../../utils/browser";
import { buildSMSShareLink, buildURLCopyShareLink } from "../../utils/urls";
import colors from "../../utils/colorConstants";
import fastpanel from "../../utils/fastpanel";

import Arrow from "../Icons/Arrow";
import Pinterest from "../Icons/Pinterest";
import Facebook from "../Icons/Facebook";
import Messages from "../Icons/Messages";
import Close from "../Icons/Close";
import Copy from "../Icons/Copy";

let FacebookAppId = 0;
switch (process.env.NODE_ENV) {
  case "development":
    FacebookAppId = 321449234589633;
    break;
  case "test":
    FacebookAppId = 1111111111111;
    break;
  case "production":
    FacebookAppId = 103384409739156;
    break;
  default:
    FacebookAppId = 321449234589633;
    break;
}

interface Props {
  className?: string;
  name: string;
  partner: any;
  images: any[];
  shareURL: string;
  onDismiss: any;
}

const ShareDrawerContents: FC<Props> = ({
  className,
  name,
  partner,
  images,
  shareURL,
  onDismiss,
}) => {
  const imageToShare = images && images[0] ? images[0] : "";
  const partnerName = partner?.meta?.label || partner?.name || "";
  const socialMessage = `Paperless Post - ${name}${
    partnerName ? ` by ${partnerName}` : ``
  }`;
  const [hasCopiedLink, setHasCopiedLink] = useState(false);
  const copyClickTargetRef = useRef(null);
  useEffect(() => setHasCopiedLink(false), [shareURL]);
  useEffect(() => {
    if (copyClickTargetRef) {
      const clipboard = new ClipboardJS(
        // @ts-expect-error
        copyClickTargetRef.current,
        { text: () => buildURLCopyShareLink(shareURL) }
      );
      clipboard.on("success", () => setHasCopiedLink(true));

      return () => clipboard.destroy();
    }
  }, [copyClickTargetRef.current]);

  return (
    <div>
      <div className="flex h-12 items-center border-b border-gallery">
        <h3 className="w-full text-center tracked-medium">Share</h3>
        <div className="absolute p-4 right-0" onClick={onDismiss}>
          <Close height="1rem" width="1rem" />
        </div>
      </div>
      <div className="flex flex-row h-36 bg-panna-cotta items-center px-6">
        <img className="max-h-24" src={imageToShare} />
        <div className="ml-6">
          <h3 className="paragraph-medium">{name}</h3>
          {partnerName && (
            <p className="paragraph-xsmall mb-6 text-dusty-gray">
              {partnerName}
            </p>
          )}
        </div>
      </div>
      {/*
          We need this no-op touchstart handler in order for
          elements' to have their :active styles respected on
          Mobile Safari. For context, see:
            https://developer.mozilla.org/en-US/docs/Web/CSS/:active
            https://stackoverflow.com/a/33681490
      */}
      <ul className="m-0 p-0 list-none" onTouchStart={() => {}}>
        <li className="cursor-pointer bg-white border-b border-alto">
          <AppLink
            className="block w-full px-6"
            href={buildSMSShareLink(
              shareURL,
              name,
              partnerName,
              isIOSDevice(),
            )}
            tracking={{
              id: "sms-share",
              name: "pbr_element_clicked",
            }}
          >
            <div className="flex w-full tracked-medium items-center justify-between h-12">
              <Messages className="mr-4" width="1.75rem" height="1.75rem" />
              <span className="w-full text-left">Messages</span>
              <Arrow fill={colors.lapel} title="Messages link" />
            </div>
          </AppLink>
        </li>
        <li className="cursor-pointer bg-white border-b border-alto">
          <PinterestButton
            className="block w-full px-6"
            message={socialMessage}
            element="a"
            media={imageToShare}
            onClick={
              () => fastpanel({
                name: "pbr_element_clicked",
                data: { "id": "pinterest-share" }
              })
            }
          >
            <div className="flex w-full tracked-medium items-center justify-between h-12">
              <Pinterest className="mr-4" width="1.5rem" height="1.5rem" />
              <span className="w-full text-left">Pinterest</span>
              <Arrow fill={colors.lapel} title="Pinterest link" />
            </div>
          </PinterestButton>
        </li>
        <li className="cursor-pointer bg-white border-b border-alto">
          {/* TODO: ADD TRACKING BACK IN */}
          <FacebookButton
            className="block w-full px-6"
            url={`${shareURL}?utm_source=mobileshare&utm_medium=facebook&utm_campaign=facebook_share`}
            appId={FacebookAppId}
            message={socialMessage}
            element="a"
            media={imageToShare}
            onClick={
              () => fastpanel({
                name: "pbr_element_clicked",
                data: { "id": "facebook-share" }
              })
            }
          >
            <div className="flex w-full tracked-medium items-center justify-between h-12">
              <Facebook
                className="mr-4"
                fill={colors.facebook}
                width="1.25rem"
                height="1.25rem"
              />
              <span className="w-full text-left">Facebook</span>
              <Arrow fill={colors.lapel} title="Facebook link" />
            </div>
          </FacebookButton>
        </li>
        <li className="cursor-pointer bg-white border-b border-alto">
          {/* TODO: ADD TRACKING BACK IN */}
          <div
            className="flex w-full tracked-medium items-center justify-between px-6 h-12"
            ref={copyClickTargetRef}
            onClick={
              () => fastpanel({
                name: "pbr_element_clicked",
                data: { "id": "copy-share" }
              })
            }
          >
            <Copy
              className="mr-4"
              strokeColor={colors.lapel}
              height="1.5rem"
              width="1.5rem"
            />
            <span className="w-full text-left">
              {hasCopiedLink ? "Link copied successfully!" : "Copy link"}
            </span>
            <Arrow fill={colors.lapel} title="Copy link" />
          </div>
        </li>
      </ul>
    </div>
  );
};

export default ShareDrawerContents;
